import { CloseOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { Button, Empty, Spin, Typography } from 'antd';

import { SupportChatComposer } from './SupportChatComposer';
import { SupportChatMessage } from './SupportChatMessage';

import type { SupportChatWindowModel } from '@features/SupportChat/types';
import type { FC } from 'react';

const { Text } = Typography;

interface SupportChatWindowProps extends SupportChatWindowModel {
  onClose: () => void;
}

export const SupportChatWindow: FC<SupportChatWindowProps> = ({
  messages,
  isLoading,
  isLoadingMore,
  messagesContainerRef,
  handleMessagesScroll,
  downloadingFileId,
  downloadAnchorRef,
  handleDownloadFile,
  onClose,
  ...composerProps
}) => {
  const renderMessages = () => {
    if (isLoading) {
      return (
        <div className="SupportChatWindow__placeholder">
          <Spin />
        </div>
      );
    }

    if (messages.length === 0) {
      return (
        <div className="SupportChatWindow__placeholder">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Напишите нам — поможем разобраться"
          />
        </div>
      );
    }

    return (
      <>
        {isLoadingMore && (
          <div className="SupportChatWindow__loading-more">
            <Spin size="small" />
          </div>
        )}
        {messages.map((message) => (
          <SupportChatMessage
            key={message.id}
            message={message}
            downloadingFileId={downloadingFileId}
            onDownloadFile={handleDownloadFile}
          />
        ))}
      </>
    );
  };

  return (
    <div
      className="SupportChatWindow"
      role="dialog"
      aria-label="Чат с технической поддержкой"
    >
      <div className="SupportChatWindow__header">
        <div className="SupportChatWindow__title">
          <CustomerServiceOutlined className="SupportChatWindow__title-icon" />
          <Text>Техническая поддержка</Text>
        </div>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          aria-label="Закрыть чат"
          onClick={onClose}
        />
      </div>

      <div
        ref={messagesContainerRef}
        className="SupportChatWindow__messages"
        onScroll={handleMessagesScroll}
      >
        {renderMessages()}
      </div>

      <SupportChatComposer {...composerProps} />

      {/* Ссылка для скачивания вложений, см. useSupportChatFileDownload */}
      <a
        ref={downloadAnchorRef}
        className="SupportChatWindow__download-anchor"
        aria-hidden
        tabIndex={-1}
      />
    </div>
  );
};
